import { NavLink, Outlet, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import FoodPulseAgent from "@/components/cms/FoodPulseAgent";
import {
  LayoutDashboard, FileText, Package, Users, Mailbox, Building2, Globe,
  Bell, AlertTriangle, Newspaper, HeartHandshake, Utensils, MapPin,
  ScrollText, Settings, ShieldCheck, Sparkles, Tag,
} from "lucide-react";

/**
 * Unified CMS shell for FoodPulse admins.
 * Left nav bundles every existing scattered admin surface under a single
 * roof. New surfaces (Pages editor, Products CRUD, User correspondence,
 * Business/Websites) will slot into their reserved sections in follow-ups.
 */

const NAV_SECTIONS = [
  {
    label: "Overview",
    items: [
      { to: "/admin/cms", end: true, icon: LayoutDashboard, label: "Dashboard" },
    ],
  },
  {
    label: "Content",
    items: [
      { to: "/admin/cms/pages", icon: FileText, label: "Pages" },
      { to: "/admin/cms/articles", icon: ScrollText, label: "Articles" },
      { to: "/admin/cms/food-news", icon: Newspaper, label: "Food News" },
      { to: "/admin/cms/recalls", icon: AlertTriangle, label: "Recalls" },
      { to: "/admin/cms/alerts", icon: Bell, label: "Alerts" },
      { to: "/admin/cms/recipes", icon: Utensils, label: "Recipes" },
    ],
  },
  {
    label: "Catalog",
    items: [
      { to: "/admin/cms/products", icon: Package, label: "Products" },
      { to: "/admin/cms/products/pending", icon: ShieldCheck, label: "Pending Approvals" },
      { to: "/admin/cms/categories", icon: Tag, label: "Categories" },
      { to: "/admin/cms/deals", icon: Sparkles, label: "Deals" },
    ],
  },
  {
    label: "People",
    items: [
      { to: "/admin/cms/users", icon: Users, label: "Users" },
      { to: "/admin/cms/correspondence", icon: Mailbox, label: "Correspondence" },
    ],
  },
  {
    label: "Businesses & Websites",
    items: [
      { to: "/admin/cms/businesses", icon: Building2, label: "Business Customers" },
      { to: "/admin/cms/nearby", icon: MapPin, label: "Nearby Businesses" },
      { to: "/admin/cms/websites", icon: Globe, label: "Customer Websites" },
      { to: "/admin/cms/directory-dedup", icon: ShieldCheck, label: "Directory Dedup" },
    ],
  },
  {
    label: "System",
    items: [
      { to: "/admin/cms/schedulers", icon: Settings, label: "Schedulers" },
      { to: "/admin/cms/community", icon: HeartHandshake, label: "Community" },
    ],
  },
];

export default function CmsLayout() {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-slate-500" data-testid="cms-loading">Loading…</div>;
  if (!user) return <Navigate to="/login?next=/admin/cms" replace />;
  const isAdmin = user.is_admin || user.platform_role === "super_admin" || user.platform_role === "admin";
  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-lg p-10 text-center" data-testid="cms-forbidden">
        <ShieldCheck className="mx-auto h-10 w-10 text-slate-300" />
        <h1 className="mt-3 text-xl font-semibold">Admin access required</h1>
        <p className="mt-2 text-slate-500">Sign in with a FoodPulse admin account to open the CMS.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="cms-layout">
      <div className="mx-auto flex max-w-[1600px]">
        {/* Sidebar */}
        <aside
          className="sticky top-0 hidden h-screen w-64 shrink-0 border-r border-slate-200 bg-white lg:block"
          data-testid="cms-sidebar"
        >
          <div className="flex h-14 items-center gap-2 border-b border-slate-200 px-4">
            <img src="/foodpulse-icon-192.png" alt="FoodPulse" className="h-7 w-7 rounded-md" />
            <div className="leading-tight">
              <div className="text-sm font-semibold text-slate-900">FoodPulse CMS</div>
              <div className="text-[11px] text-slate-500">Manage everything in one place</div>
            </div>
          </div>
          <nav className="flex flex-col gap-4 overflow-y-auto p-3 pb-16">
            {NAV_SECTIONS.map((section) => (
              <div key={section.label}>
                <div className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  {section.label}
                </div>
                {section.items.map((it) => {
                  const Icon = it.icon;
                  return (
                    <NavLink
                      key={it.to}
                      to={it.to}
                      end={it.end}
                      data-testid={`cms-nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        `flex items-center gap-2 rounded-md px-2 py-1.5 text-sm ${
                          isActive
                            ? "bg-emerald-50 text-emerald-700 font-medium"
                            : "text-slate-700 hover:bg-slate-100"
                        }`
                      }
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span>{it.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            ))}
          </nav>
        </aside>

        {/* Mobile top-bar nav */}
        <div className="lg:hidden fixed top-0 left-0 right-0 z-20 flex items-center gap-2 border-b border-slate-200 bg-white px-3 py-2">
          <img src="/foodpulse-icon-192.png" alt="FoodPulse" className="h-6 w-6 rounded" />
          <span className="text-sm font-semibold text-slate-900">FoodPulse CMS</span>
        </div>

        {/* Content pane */}
        <main className="flex-1 min-h-screen p-4 pt-14 lg:p-8 lg:pt-8" data-testid="cms-main">
          <Outlet />
        </main>
      </div>
      {/* FoodPulse Agent — floating AI assistant grounded on the current
          CMS route + a lightweight retrieval over the food_items catalog. */}
      <FoodPulseAgent />
    </div>
  );
}
